import {memo} from 'react';
import {useTranslation} from 'react-i18next';
import {ChevronDown, Forward} from 'lucide-react';
import {cn, useModalControls} from '@chekin/guestapp-ui-kit';
import {useReservationSettings} from 'hooks';
import ModalSheet from 'components/common/ModalSheet';
import ShareRegistrationLink from 'components/ModalSheets/ShareRegistrationLink';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useReservation} from 'context/reservation';
import ChekinProSettings from 'ChekinProSettings';

type Props = {
  currentTotalGuest: number;
  totalGuest: number;
  onGuestCountClick: () => void;
};
const HeaderGuestInformation = memo(
  ({currentTotalGuest, totalGuest, onGuestCountClick}: Props) => {
    const {t} = useTranslation();
    const {isOpen, closeModal, openModal} = useModalControls();
    const showShareButton =
      useLayoutMode() !== 'kiosko' && ChekinProSettings.canShareRegistrationLink;

    const {allowEditingGuestNumber} = useReservation();
    const {checkCanEditReservationDetails} = useReservationSettings();
    const canEditReservationDetails = checkCanEditReservationDetails();
    const canEditGuestNumber = canEditReservationDetails && allowEditingGuestNumber;
    return (
      <>
        <div
          className={cn(
            'w-full flex items-center space-x-2 justify-between ',
            'absolute -top-6 left-0  bg-[var(--primary-10)] p-6',
            'md:static md:bg-transparent md:py-4 md:px-0',
          )}
        >
          <button
            onClick={() => (canEditGuestNumber ? onGuestCountClick() : undefined)}
            className="min-w-fit flex items-center gap-1 bg-transparent border-none p-0 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <p className="font-semibold text-base text-[var(--button-primary-background)] lowercase">
              {currentTotalGuest} {t('of')} {totalGuest} {t('guests')}
            </p>
            {canEditGuestNumber && (
              <ChevronDown
                className="w-4 h-4 flex-shrink-0 "
                style={{color: 'var(--button-primary-background)', strokeWidth: '3px'}}
              />
            )}
          </button>

          <div className="w-full h-[1.5px] bg-[var(--button-primary-background)]" />

          {showShareButton && (
            <button
              onClick={openModal}
              className="flex items-center gap-2 bg-transparent border-none p-0 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <Forward
                className="w-5 h-5 flex-shrink-0"
                style={{color: 'var(--button-primary-background)'}}
                strokeWidth={2}
              />
              <p className="font-semibold text-base text-[var(--button-primary-background)]">
                {t('share')}
              </p>
            </button>
          )}
        </div>
        <div className="md:hidden h-10 w-full"></div>

        <ModalSheet
          isOpenModalSheet={isOpen}
          handleCloseModal={closeModal}
          withCloseButton
          maxWidth={'375px'}
        >
          <ShareRegistrationLink />
        </ModalSheet>
      </>
    );
  },
);

HeaderGuestInformation.displayName = 'HeaderGuestInformation';

export {HeaderGuestInformation};
